module Meevo.Plugins.InventoryReturns {
    "use strict";

    import IInventoryCountProductSearchCriteria = Meevo.Model.IInventoryCountProductSearchCriteria;
    import IInventoryReturn = Meevo.Model.IInventoryReturn;
    import IInventoryReturnLineItem = Meevo.Model.IInventoryReturnLineItem;
    import ProductInventoryTypeEnum = Meevo.Model.ProductInventoryTypeEnum;
    import ReturnReasonEnum = Meevo.Model.ReturnReasonEnum;

    /**
    * Main Section of Client 
    */
    export class MainSection extends Meevo.Components.BaseClasses.DataMaintenanceSectionBase<IInventoryReturn> {
        public referenceNumberRegExp: RegExp = new RegExp('^[a-zA-Z0-9]{0,30}$');
        public isDiscountinued: boolean = false;
        public inventoryCountProductCriteria: IInventoryCountProductSearchCriteria = {
            productObjectState: Meevo.Model.ObjectStateEnum.Active,
            variantObjectState: [Meevo.Model.ObjectStateEnum.Active],
            isInventoryTrackable: true,
            floorLocationId: null,
            id: null,
            inventoryCountOnly: false,
            iscounted: null,
            productInventoryType: ProductInventoryTypeEnum.Retail,
            manufacturerIds: [],
            distributorIds: [],
            productCatIds: []
        };
        public itemTypeList = [
            { value: ProductInventoryTypeEnum.Retail, label: 'LABEL_RETAIL' },
            { value: ProductInventoryTypeEnum.ShopSupply, label: 'LABEL_SHOP_SUPPLY' },
            { value: ProductInventoryTypeEnum.Other, label: 'HERO_SUB_MISC_SUPPLIES' }
        ];

        public shopSupply = ProductInventoryTypeEnum.ShopSupply;

        public customFooterInfo: Meevo.Components.Controls.IMaintenanceFormCustomFooterInfo = {
            className: '',
            template: 'customFooterInfo.html',
            visibilityRules: () => true,
            position: 'left'
        };
        public selectedInventoryProduct: IInventoryReturnLineItem;
        public productInventoryType = {
            [ProductInventoryTypeEnum.Retail]: this.$translate.instant("LABEL_RETAIL"),
            [ProductInventoryTypeEnum.ShopSupply]: this.$translate.instant("LABEL_SHOP_SUPPLY"),
            [ProductInventoryTypeEnum.Other]: this.$translate.instant("HEADER_MISCELLANEOUS_SUPPLY_DEFINITIONS")
        };
        public subtotal: number = 0;

        protected decimalPrecision: number;
        public tenantEnableProductUsageTracking: boolean = false;

        private variantLotCriteria: any = {}; // hash by variant_TId

        constructor(
            sectionId: number,
            displayNameContentKey: string,
            template: string,
            isPrimarySection: boolean,
            handlesOwnCrud: boolean,
            baseEndpoint: string,
            sectionEndpoint: string,
            loggingService: Meevo.Components.Logging.ILoggingService,
            dataService: Meevo.Components.BaseClasses.IDataService,
            identityService: Meevo.Components.Common.IIdentityService,
            noun: Meevo.Model.NounEnum,
            appSettings: Components.Common.IAppSettings,
            protected params,
            protected modalService: Meevo.Components.Modal.IModalService,
            protected busyService: Meevo.Components.Busy.IBusyService,
            protected $translate: ng.translate.ITranslateService,
            protected $filter: angular.IFilterService,
            sectionSecurityKey?: string
        ) {
            super(
                sectionId,
                displayNameContentKey,
                template,
                isPrimarySection,
                handlesOwnCrud,
                baseEndpoint,
                sectionEndpoint,
                loggingService,
                dataService,
                identityService,
                noun,
                appSettings,
                sectionSecurityKey);

            this.decimalPrecision = this.appSettings.APP_BUSINFOSETTINGS.generalSettings.inventoryDecimalPrecision || 4;
            this.tenantEnableProductUsageTracking = this.appSettings.APP_BUSINFOSETTINGS.generalSettings.productUsageTracking || false;
        }

        sectionModelRetrieved(): void {
            if (!this.model) return;
            let filter: any = this.$filter;
            let meevoDate = filter('meevoDateFilter');
            let today = moment().startOf('day');

            if (this.model.insertItems && this.model.insertItems.length) {
                let keys = {};
                this.model.insertItems.forEach((i: IInventoryReturnLineItem) => {
                    i.wholeSale = i.lineTotal / i.returnQuantity;
                    if (i.trackLots) {
                        let isExpired = i.expirationDate ? moment(i.expirationDate).isBefore(today) : false;
                        i.variantLot = {
                            variant_TId: i.variant_TId,
                            variantLot_TId: i.variantLot_TId,
                            lotNumber: i.lotNumber,
                            expirationDate: i.expirationDate,
                            onHand: i.onHand,
                            isExpired: isExpired,
                            expirationDateDisplay: i.expirationDate ?`${this.$translate.instant('LABEL_EXPIRATION_DATE_PREFIX')}${meevoDate(i.expirationDate)}` + (isExpired ? ` (${this.$translate.instant('LABEL_EXPIRED')})` : '') : ''
                        };
                        this.getVariantLotCriteria(i);
                        if (!keys.hasOwnProperty(i.variant_TId + '-' + i.productInventoryType)) {
                            i.isFirst = true;
                            keys[i.variant_TId + '-' + i.productInventoryType] = i;
                        }
                    }
                    else {
                        i.isFirst = true;
                    }
                });

                this.calculateSubTotal();
            }

            this.model.deleteItems = [];
            this.distributorChanged();
            this.productInventoryTypeChanged();
        }

        /**
        * Base class override which defines what a DEFAULT model looks like.
        */
        createSectionModelInstance(postAdd: (data: any) => any): void {
            this.dataService.getTemplate(this.baseEndpoint).then((data) => {
                this.model = data;
                this.model.insertItems = [];
                this.model.deleteItems = [];
                this.model.tax = 0;
                this.model.distributor = null;
                this.model.productInventoryType = ProductInventoryTypeEnum.Retail;
            });
        }

        /**
         * User has selected another distributor
         */
        public distributorChanged(): void {
            this.selectedInventoryProduct = null;
            if (this.model.distributor) {
                this.inventoryCountProductCriteria.distributorIds.length = 0;
                this.inventoryCountProductCriteria.distributorIds.push(this.model.distributor.id);
            }
        }

        public productInventoryTypeChanged(): void {
            this.selectedInventoryProduct = null;
            this.inventoryCountProductCriteria.productInventoryType = this.model.productInventoryType ? this.model.productInventoryType : ProductInventoryTypeEnum.Retail;
        }

        /**
        * Calculate subtotal
        */
        public calculateSubTotal(): void {
            this.subtotal = this.model.insertItems.reduce((sum, item) => sum + Number(item.lineTotal), 0);
        }

        /**
         * Calculate total
         * @param {number} subtotal
         * @returns {number}
         */
        public calculateTotal(subtotal: number): number {
            if (!this.model) return;

            return Number(subtotal) + Number(this.model.tax);
        }

        /**
         * Calculate total line 
         * @param {IInventoryReturnLineItem} item
         */
        public calculateTotalLine(item: IInventoryReturnLineItem): void {
            item.lineTotal = item.returnQuantity * item.wholeSale;
            this.calculateSubTotal();

            if (item.onHand !== null && Number(item.returnQuantity) > Number(item.onHand)) {
                this.modalService.alert('MESSAGE_YOU_ARE_RETURNING_GREATER_THEN_ON_HAND', Components.Modal.ModalTranslationModes.Translate);
            }
        }

        /**
         * Calculate Sale line
         * @param {IInventoryReturnLineItem} item
         */
        public calculateSaleLine(item: IInventoryReturnLineItem): void {
            item.wholeSale = item.lineTotal / item.returnQuantity;
            this.calculateSubTotal();
        }

        /**
         * Add new product to inventory return
         */
        public addNewProduct(subItem: IInventoryReturnLineItem = null): void {
            var lineItem: IInventoryReturnLineItem;
            if (subItem) {
                let variantId = subItem.variant_TId;
                lineItem = { ...subItem, isFirst: false, variantLot: null, variantLot_TId: null, onHand: null };
                // look for the last index for an item with same variantId; want to group same variants together
                let lastIndex: number = -1;
                for (let i = this.model.insertItems.length - 1; i >= 0; i--) {
                    if (this.model.insertItems[i].variant_TId === variantId) {
                        lastIndex = i;
                        break;
                    }
                }
                if (lastIndex === -1) {
                    // fallback: append to end if variant not found
                    this.model.insertItems.push(lineItem);
                }
                else {
                    this.model.insertItems.splice(lastIndex + 1, 0, lineItem);
                }
            }
            else {
                lineItem = {
                    productInventoryType: this.model.productInventoryType,
                    variant_TId: this.selectedInventoryProduct.variantId,
                    onHand: this.selectedInventoryProduct.onHand,
                    variantOnHand: this.selectedInventoryProduct.onHand,
                    productName: this.selectedInventoryProduct.productName,
                    wholeSale: this.selectedInventoryProduct.wholeSale,
                    displayName: this.selectedInventoryProduct.displayName,
                    product_TId: this.selectedInventoryProduct.product_TId,
                    modelQuantity: this.selectedInventoryProduct.modelQuantity,
                    orderPoint: this.selectedInventoryProduct.orderPoint,
                    sku: this.selectedInventoryProduct.sku,
                    barCode: this.selectedInventoryProduct.barCode,
                    enableProductUsageTracking: this.selectedInventoryProduct.enableProductUsageTracking,
                    productUsageType: this.selectedInventoryProduct.productUsageType,
                    productUnitOfMeasureEnum: this.selectedInventoryProduct.productUnitOfMeasureEnum,
                    unitOfMeasureQuantity: this.selectedInventoryProduct.unitOfMeasureQuantity,
                    trackLots: this.selectedInventoryProduct.trackLots,
                    trackExpirationDate: this.selectedInventoryProduct.trackExpirationDate,
                    isFirst: true
                };
                this.model.insertItems.push(lineItem);

                let product_TId = this.selectedInventoryProduct.product_TId;

                // clear selected product
                this.selectedInventoryProduct = null;

                this.busyService.start();
                // get particular product in order to use description
                this.dataService.getItem(this.appSettings.API_ENDPOINTS.product, product_TId)
                    .then((data: any) => {
                        // update description for product
                        lineItem.productDescription = data.description;
                    })
                    .finally(() => {
                        this.busyService.stop();
                    });
            }

            lineItem.isNew = true;
            lineItem.isDeducted = true;
            lineItem.returnQuantity = 1;

            this.calculateTotalLine(lineItem);
            this.calculateSubTotal();
        }

        /**
         * Remove product variant
         * @param {IInventoryReturnLineItem} item
         */
        public removeProductVariant(item: IInventoryReturnLineItem): void {
            let index = this.model.insertItems.indexOf(item);
            const variantId = item.variant_TId;
            const variantIdStr = variantId.toString();

            if (index > -1) {
                let foundNext: boolean = false;
                if (item.isFirst) {
                    let nextItem = this.model.insertItems.find(ni => ni.variant_TId === variantId && !ni.isFirst);
                    if (nextItem) {
                        foundNext = true;
                        nextItem.isFirst = true;
                    }
                }

                this.model.insertItems.splice(index, 1);

                if (foundNext || !item.isFirst) {
                    let items = this.model.insertItems.filter(ni => ni.variant_TId === variantId && ni.variantLot_TId);
                    this.variantLotCriteria[variantIdStr].excludeIdList = items.map(i => i.variantLot_TId);
                }
                else {
                    delete this.variantLotCriteria[variantIdStr];
                }

                if (!item.isNew) {
                    this.model.deleteItems.push(item);
                }

                this.calculateSubTotal();
            }
        }

        /**
         * Remove product variant
         * @param {IInventoryReturnLineItem} item
         */
        public removeProductVariantById(variant_TId: Guid): void {
            if (!variant_TId) {
                return;
            }

            delete this.variantLotCriteria[variant_TId.toString()];

            var index: number = -1;
            do
            {
                index = this.model.insertItems.findIndex(item => item.variant_TId === variant_TId);
                if (index > -1) {
                    var items = this.model.insertItems.splice(index, 1);

                    if (!items[0].isNew) {
                        this.model.deleteItems.push(items[0]);
                    }
                }
            }
            while (index > -1);

            this.calculateSubTotal();
        }

        /**
         * Variant object state setuping
         */
        public discountinuedChanged(): void {
            if (this.isDiscountinued) {
                this.inventoryCountProductCriteria.variantObjectState = [
                    Meevo.Model.ObjectStateEnum.Active,
                    Meevo.Model.ObjectStateEnum.Inactive
                ];
            } else {
                this.inventoryCountProductCriteria.variantObjectState = [
                    Meevo.Model.ObjectStateEnum.Active
                ];
            }
        }

        isProductUsageEnabled(variant: IInventoryReturnLineItem): boolean {
            return this.tenantEnableProductUsageTracking && variant && variant.enableProductUsageTracking && variant.productUsageType == Meevo.Model.ProductUsageAmountTypeEnum.PartialAmount;
        }

        getOnHandDecimalPrecision(item: IInventoryReturnLineItem): number {
            return (!Number.isInteger(item.onHand) || this.isProductUsageEnabled(item)) ? this.decimalPrecision : null;
        }

        getVariantLotCriteria(subItem: IInventoryReturnLineItem) {
            if (!subItem || !subItem.variant_TId) {
                return;
            }

            const variantId = subItem.variant_TId;
            const variantIdStr = variantId.toString();

            if (!this.variantLotCriteria[variantIdStr]) {
                this.variantLotCriteria[variantIdStr] = {
                    variant_TId: variantId,
                    productInventoryType: subItem.productInventoryType,
                    filter: '',
                    excludeIdList: []
                }
            }

            this.refreshExclusionList(subItem);

            return this.variantLotCriteria[variantIdStr];
        }

        refreshExclusionList(subItem: IInventoryReturnLineItem) {
            if (!subItem || !subItem.variant_TId) {
                return;
            }

            const variantId = subItem.variant_TId;
            const variantIdStr = variantId.toString();

            if (!this.variantLotCriteria[variantIdStr]) {
                return;
            }

            let items = this.model.insertItems.filter(ni=> ni.variant_TId === variantId && ni.variantLot_TId);
            this.variantLotCriteria[variantIdStr].excludeIdList = items.map(i => i.variantLot_TId);
        }

        getVariantLotInfo(subItem: IInventoryReturnLineItem) {
            if (subItem.variantLot) {
                subItem.variantLot_TId = subItem.variantLot.variantLot_TId;
                subItem.onHand = subItem.variantLot.onHand;
            }
            else {
                subItem.variantLot_TId = null;
                subItem.onHand = null;
            }
            this.refreshExclusionList(subItem);
        }

        updateDeductOnAllLots(subItem: IInventoryReturnLineItem) {
            this.model.insertItems.filter(ni => ni.variant_TId === subItem.variant_TId)
                                  .forEach((item, ndx, arr) => item.isDeducted = subItem.isDeducted);
        }

        lotIsExpired(subItem) {
            return (subItem && subItem.variantLot && subItem.variantLot.isExpired);
        }
    }
}